// The call surface: join the call, start the listener, watch the checklist
// fill in, hang up.
//
// Spec s9: the session is the tab. It starts when the user joins a call and the
// feature is enabled for that call, and stops on call end, tab close, or
// toggle-off. Nothing survives.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AudioSource } from '../audio/AudioSource';
import { FileAudioSource } from '../audio/sources/FileAudioSource';
import { MicAudioSource } from '../audio/sources/MicAudioSource';
import { RemoteTracksAudioSource } from '../audio/sources/RemoteTracksAudioSource';
import { liveSwitchBridge, useLiveSwitchTrackCount } from '../liveswitch/bridge';
import { LiveSwitchCall, type Participant } from '../liveswitch/call';
import type { ChecklistConfig } from '../config/types';
import { saveSession, startSession } from '../inspections/saveSession';
import { InviteButton } from './InviteButton';
import { clearDraft, draftOverrides, loadDraft, saveDraft } from '../pipeline/draft';
import { hasValidSession } from '../auth/session';
import type { FieldOverride, FieldState } from '../pipeline/extraction';
import { FIELD, ORGS } from '../pricing/scope';
import { DeepgramTranscriber } from '../pipeline/DeepgramTranscriber';
import { Session, type SessionStatus } from '../pipeline/Session';
import type { ExtractionUsage } from '../pipeline/extractionClient';
import type { TranscriptSegment } from '../pipeline/Transcriber';
import { guestLink, newCallId, syncCallIdToUrl } from '../routing';
import { CallStage } from './CallStage';
import { ChecklistPanel } from './ChecklistPanel';
import { MediaControls } from './MediaControls';
import { TranscriptView } from './TranscriptView';

type SourceKind = 'liveswitch' | 'file' | 'mic';

type Props = {
  config: ChecklistConfig | null;
  configReady: boolean;
  /** Checklists are still being fetched, so "none in use" is not yet the truth. */
  configLoading: boolean;
/**
   * Called when a call starts or ends, so the app can mark the inspector busy.
   * Passed in rather than reached for: presence outlives this component, which
   * is hidden rather than unmounted whenever another tab is showing.
   */
  onCallActive?: (active: boolean) => void;
  /**
   * A room chosen in the lobby. Fills the call id in and leaves Join to the
   * person, who may want a moment before they are on camera.
   */
  pickedCallId?: string | null;
  /** The company the picked room came with, if it carried one. */
  pickedOrgId?: string | null;
  onStatus: (status: SessionStatus) => void;
  /** Call named by ?call= in the URL, so a refresh rejoins the same channel. */
  initialCallId: string | null;
};

/**
 * Until `/liveswitch-token` is authenticated, the channel ID is the only thing
 * between a stranger and the call, so a new one is unguessable by default.
 * Joining an existing call means pasting the ID the other side is using.
 */
export function CallView({
  config,
  configReady,
  configLoading,
  onStatus,
  onCallActive,
  initialCallId,
  pickedCallId,
  pickedOrgId,
}: Props) {
  const remoteTrackCount = useLiveSwitchTrackCount();
  const [sourceKind, setSourceKind] = useState<SourceKind>('liveswitch');
  const [file, setFile] = useState<File | null>(null);

  // Read once, before any state is initialised from it. A draft is what the
  // last session was in the middle of when it lost the page - a revoked token,
  // a reload, a closed tab - and the customer has already said all of it.
  const draft = useMemo(() => loadDraft(), []);

  const [channelId, setChannelId] = useState(
    () => initialCallId ?? draft?.callId ?? newCallId(),
  );
  const [copied, setCopied] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [inCall, setInCall] = useState(false);
  const [joining, setJoining] = useState(false);
  const [recording, setRecording] = useState(false);

  const [status, setStatus] = useState<SessionStatus>('idle');
  const [state, setState] = useState<FieldState[]>(() => draft?.state ?? []);
  // Answers the person on the call gave, kept apart from the model's output so
  // the seven-second recompute cannot wipe them.
  const [overrides, setOverrides] = useState<Map<string, FieldOverride>>(() =>
    draft ? draftOverrides(draft) : new Map(),
  );
  const [inFlight, setInFlight] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<(ExtractionUsage & { calls: number }) | null>(null);
  const [partial, setPartial] = useState('');
  const [segments, setSegments] = useState<TranscriptSegment[]>(() => draft?.segments ?? []);
  const [sttSeconds, setSttSeconds] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  // When the inspector joined. elapsedMs drives the on-screen counter, but the
  // record needs a wall-clock time, and the two are not the same thing once a
  // call has been sitting idle.
  const joinedAtRef = useRef<Date | null>(null);
  const [saveProblem, setSaveProblem] = useState<string | null>(null);

  // What a save would send right now. Kept in a ref because the pagehide
  // listener is registered once and would otherwise close over the state as it
  // was when the call started.
  const latestRef = useRef({ state, overrides, segments, elapsedMs, channelId });
  latestRef.current = { state, overrides, segments, elapsedMs, channelId };

  // A room picked in the lobby fills the field in. Ignored while a call is
  // running: pulling the id out from under someone mid-call would be worse
  // than making them come back to it.
  useEffect(() => {
    if (pickedCallId && !inCall) setChannelId(pickedCallId);
  }, [pickedCallId, inCall]);

  // Written as the call fills in, so what is on disk is never far behind what
  // is on screen. Cheap: a few kilobytes of text, changing only when an
  // extraction pass lands or somebody answers a field.
  useEffect(() => {
    if (!joinedAtRef.current) return;
    saveDraft({
      callId: channelId.trim(),
      joinedAt: joinedAtRef.current.toISOString(),
      elapsedMs,
      state,
      overrides: [...overrides],
      segments,
    });
  }, [channelId, elapsedMs, state, overrides, segments]);

  const sessionRef = useRef<Session | null>(null);
  const callRef = useRef<LiveSwitchCall | null>(null);
  // The config the running session reads. Kept in a ref so switching checklists
  // mid-call takes effect on the next pass without restarting anything.
  const configRef = useRef<ChecklistConfig | null>(config);
  configRef.current = config;

  const setBoth = useCallback(
    (next: SessionStatus) => {
      setStatus(next);
      onStatus(next);
    },
    [onStatus],
  );

  const buildSource = useCallback((): AudioSource => {
    switch (sourceKind) {
      case 'liveswitch':
        return new RemoteTracksAudioSource(liveSwitchBridge.feed);
      case 'mic':
        return new MicAudioSource();
      case 'file':
        if (!file) throw new Error('Choose an audio file first.');
        return new FileAudioSource(file);
    }
  }, [sourceKind, file]);

  const stopListening = useCallback(async () => {
    const session = sessionRef.current;
    sessionRef.current = null;
    if (!session) return;
    await session.stop();
    // The transcript, the checklist and the price survive hanging up: the
    // minute after a call is when you actually read them. Only the partial
    // goes, because a half-heard phrase that will never be completed is not
    // worth keeping.
    //
    // Spec s2 has extracted state die with the session. It now dies with the
    // next call or the tab instead - still nothing saved, exported or sent
    // anywhere, but no longer snatched away at the moment it is most useful.
    setPartial('');
  }, []);

  const startListening = useCallback(async () => {
    if (sessionRef.current) return;
    setError(null);

    let source: AudioSource;
    try {
      source = buildSource();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }

    const transcriber = new DeepgramTranscriber();
    const session = new Session(source, transcriber, () => configRef.current, {
      onStatus: setBoth,
      onState: setState,
      onInFlight: setInFlight,
      onUsage: setUsage,
      onError: (message) => setError(message),
      onPartial: setPartial,
      onFinal: (segment) => {
        setPartial('');
        setSegments((prev) => [...prev, segment]);
      },
    });
    sessionRef.current = session;

    try {
      await session.start();
    } catch {
      sessionRef.current = null;
    }
  }, [buildSource, setBoth]);

  const join = useCallback(async () => {
    if (callRef.current?.isJoined || joining) return;
    setError(null);

    // Checked here, not just when saving. The token lasts 24 hours with no
    // refresh, so it lapses while the app sits open - and a call joined on a
    // dead one runs perfectly well right up to the end, then fails to save.
    // Finding out after the customer has hung up is too late to do anything
    // about it; finding out before the call costs one sign-in.
    if (!hasValidSession()) {
      setError(
        'Your sign-in has expired. Sign in again before starting a call, or nothing from it will be saved.',
      );
      return;
    }

    setJoining(true);

    // A new call starts blank. This is the only place results are cleared, so
    // the previous call's checklist cannot bleed into this one - which would be
    // far worse than losing it, since a stale value looks exactly like a fresh
    // one.
    setState([]);
    // Blank except for what the room already told us. The company is not a call
    // result - it came from whoever raised the room - so clearing it would only
    // mean asking an estimator for an answer we were handed. Guarded against an
    // id the pricing scope does not offer, which would leave the picker blank
    // and look like nothing was passed at all.
    const seeded = new Map<string, FieldOverride>();
    if (pickedOrgId && ORGS.some((org) => org.id === pickedOrgId)) {
      seeded.set(FIELD.org, { value: pickedOrgId, source: 'typed' });
    }
    setOverrides(seeded);
    setSegments([]);
    setPartial('');
    setElapsedMs(0);
    setSaveProblem(null);
    // The previous call's draft belongs to the previous call.
    clearDraft();

    const call = new LiveSwitchCall({
      onParticipants: setParticipants,
      onError: (message) => setError(message),
      onRecording: setRecording,
    });
    callRef.current = call;

    try {
      await call.join(channelId.trim(), displayName.trim());
      setInCall(true);
      // Put the call in the address bar: without this a mid-call refresh mints
      // a new ID and silently strands whoever is already in the old channel.
      syncCallIdToUrl(channelId.trim());
      const joinedAt = new Date();
      joinedAtRef.current = joinedAt;
      onCallActive?.(true);
      // On record immediately. A call that ends by the tab closing, the phone
      // sleeping, or a crash still has to be findable afterwards - and waiting
      // for a tidy exit means the ones that go wrong are exactly the ones that
      // vanish. Not awaited: a slow write must not delay the call starting.
      void startSession(channelId.trim(), joinedAt, configRef.current).then(({ problem }) =>
        setSaveProblem(problem),
      );
      setSourceKind('liveswitch');
      // Spec s9: the listener starts when the user joins a call and the feature
      // is enabled for it. Without a checklist in use there is nothing to
      // extract, so it waits.
      if (configReady) void startListening();
    } catch (err) {
      callRef.current = null;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setJoining(false);
    }
  }, [channelId, displayName, joining, configReady, startListening]);

  const leave = useCallback(async () => {
    await stopListening();
    const call = callRef.current;
    callRef.current = null;
    setInCall(false);
    setParticipants([]);
    setRecording(false);

    // Released here, before anything is awaited.
    //
    // setInCall(false) has already put the Join button back, and the save below
    // is a network round trip - so a spent call id sat in the field for as long
    // as that took. Pressing Join in that window rejoined the call that had
    // just been completed, which then refused an invitation and would not
    // appear in the lobby. It happened on a real call.
    //
    // Safe to move: the save reads the call id captured when this callback was
    // created, not the state being replaced here.
    syncCallIdToUrl(null);
    setChannelId(newCallId());

    // Written down before hanging up the connection, not after: leave() can
    // throw, and losing the record of a call because the disconnect was untidy
    // would be the wrong way round. The panel keeps showing everything either
    // way - it is only cleared when the next call starts.
    const { problem } = await saveSession({
      callId: channelId.trim(),
      startedAt: joinedAtRef.current,
      endedAt: new Date(),
      durationSeconds: Math.round(elapsedMs / 1000),
      config: configRef.current,
      state,
      overrides,
      segments,
    });
    setSaveProblem(problem);
    // Kept when the save failed. That is precisely when the only surviving copy
    // of the call is the one on disk, and it is what lets a re-login pick the
    // checklist up rather than start it again.
    if (!problem) clearDraft();
    joinedAtRef.current = null;

    onCallActive?.(false);

    if (call) await call.leave();
  }, [stopListening, channelId, elapsedMs, state, overrides, segments]);

  // Tab close ends both the call and the session (spec s9).
  useEffect(() => {
    const onUnload = () => {
      // Best effort, and only if a call was actually joined. keepalive lets the
      // request outlive the page; browsers cap such a body at 64KB, so a very
      // long transcript may not make it. The row itself already exists from
      // join, so the worst case is a record without its transcript rather than
      // no record at all.
      if (joinedAtRef.current) {
        const now = latestRef.current;
        void saveSession(
          {
            callId: now.channelId.trim(),
            startedAt: joinedAtRef.current,
            endedAt: new Date(),
            durationSeconds: Math.round(now.elapsedMs / 1000),
            config: configRef.current,
            state: now.state,
            overrides: now.overrides,
            segments: now.segments,
          },
          true,
        );
      }
      void sessionRef.current?.stop();
      void callRef.current?.leave();
    };
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('pagehide', onUnload);
      onUnload();
    };
  }, []);

  useEffect(() => {
    if (status !== 'running') return;
    const timer = setInterval(() => {
      setSttSeconds(sessionRef.current?.sttSeconds ?? 0);
      setElapsedMs(sessionRef.current?.elapsedMs ?? 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [status]);

  const listening = status === 'running' || status === 'starting';
  const canListen = configReady && (sourceKind !== 'file' || Boolean(file));

  const sourceNote = useMemo(() => {
    // Results outlive the call now, so the header must stop describing a live
    // source once nothing is being listened to. "remote audio only · nobody
    // attached yet" over a full transcript reads like a fault.
    if (!listening && segments.length > 0) return 'from the last session';

    switch (sourceKind) {
      case 'liveswitch':
        return remoteTrackCount > 1
          ? `both sides · ${remoteTrackCount} audio sources`
          : remoteTrackCount === 1
            ? 'your microphone · nobody else on the call yet'
            : 'no audio sources yet';
      case 'file':
        return 'from a recording';
      case 'mic':
        return 'local microphone · development only';
    }
  }, [sourceKind, remoteTrackCount, listening, segments.length]);

  const placeholder = useMemo(() => {
    if (sourceKind === 'mic') return 'Microphone — development source, not the production path';
    if (sourceKind === 'file') {
      return file ? file.name : 'Choose a recording to play through the pipeline';
    }
    return joining ? 'Connecting to the call…' : 'Start or join a call to bring people in';
  }, [sourceKind, file, joining]);

  return (
    <div className="grid">
      <div>
        <CallStage
          participants={participants}
          inCall={inCall}
          placeholder={placeholder}
          status={inCall ? 'Live call' : joining ? 'Joining' : 'Not in a call'}
        />

        {recording && (
          <div className="recbadge recbadge--inline" role="status">
            <span className="recbadge__dot" /> Recording
          </div>
        )}

        {inCall && (
          <div className="callbar">
            <MediaControls
              call={callRef.current}
              onRequestSwap={() => callRef.current?.requestCameraSwap()}
              onLeave={() => void leave()}
            />
          </div>
        )}

        {sourceKind === 'liveswitch' && (
          <div className="joinbar">
            {/* In a call this field stops being an editable ID and becomes the
                guest link: the thing anyone actually needs to hand over, visible
                rather than hidden behind a copy button that reveals nothing. */}
            <label className="joinbar__field">
              <span className="eyebrow">{inCall ? 'Link to send the customer' : 'Call ID'}</span>
              <input
                value={inCall ? guestLink(channelId.trim()) : channelId}
                readOnly={inCall}
                spellCheck={false}
                onFocus={(e) => inCall && e.currentTarget.select()}
                onChange={(e) => setChannelId(e.target.value)}
              />
            </label>

            <label className="joinbar__field joinbar__field--name">
              <span className="eyebrow">Your name</span>
              <input
                value={displayName}
                placeholder="Adam"
                readOnly={inCall}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </label>

            {inCall ? (
              <>
                <button
                  type="button"
                  className="ghostbtn"
                  onClick={() => {
                    void navigator.clipboard?.writeText(guestLink(channelId.trim()));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </button>
                {/* The same invitation the lobby sends, for the case the lobby
                    cannot cover: already on the call, customer not here yet.
                    Under Copy link because it is the same job done properly -
                    copying leaves the sending to whatever the inspector has to
                    hand, which is their own phone. */}
                <InviteButton callId={channelId.trim()} />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="play"
                  disabled={joining || !channelId.trim()}
                  onClick={() => void join()}
                >
                  {joining ? 'Joining…' : 'Join call'}
                </button>
                <button
                  type="button"
                  className="ghostbtn"
                  onClick={() => setChannelId(newCallId())}
                  title="Generate a fresh, unguessable call ID"
                >
                  New ID
                </button>
              </>
            )}

            <p className="note">
              {inCall
                ? 'Send the guest link to the customer — it opens the call on their phone with no sign-in.'
                : 'Paste a call ID to join an existing call, or start a new one and share the guest link once you are in.'}
            </p>

            {/* The inspector is on the recording too, and should be told
                before joining rather than after. */}
            {!inCall && (
              <p className="consent">
                Calls are recorded, video and audio, for both sides. The customer is told the
                same before they join.
              </p>
            )}
          </div>
        )}

        <TranscriptView segments={segments} partial={partial} sourceNote={sourceNote} />

        <div className="transport">
          <button
            type="button"
            className="play"
            disabled={!listening && !canListen}
            onClick={() => (listening ? void stopListening() : void startListening())}
          >
            {listening ? 'Stop listening' : 'Start listening'}
          </button>

          <div className="picker">
            <select
              aria-label="Audio source"
              value={sourceKind}
              disabled={listening || inCall}
              onChange={(e) => setSourceKind(e.target.value as SourceKind)}
            >
              <option value="liveswitch">LiveSwitch call audio</option>
              <option value="file">Audio file (test)</option>
              <option value="mic">Microphone (dev)</option>
            </select>

            {sourceKind === 'file' && (
              <input
                type="file"
                aria-label="Recording"
                accept="audio/*"
                disabled={listening}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            )}
          </div>

          <button
            type="button"
            className="ghostbtn"
            disabled={!listening}
            onClick={() => void sessionRef.current?.refresh()}
          >
            Check now
          </button>

          {!configReady && (
            <p className="note">
              {configLoading
                ? 'Loading checklists…'
                : 'No checklist is in use. Create one under Edit checklist and mark it in use.'}
            </p>
          )}
          {error && (
            <p className="note" role="status">
              {error}
            </p>
          )}
          {saveProblem && (
            <p className="note" role="status">
              {saveProblem} The transcript and checklist are still on screen.
            </p>
          )}
        </div>
      </div>

      <ChecklistPanel
        fields={config?.fields ?? []}
        config={config}
        state={state}
        overrides={overrides}
        onAnswer={(fieldId, value, source) =>
          setOverrides((prev) => new Map(prev).set(fieldId, { value, source }))
        }
        onClearAnswer={(fieldId) =>
          setOverrides((prev) => {
            const next = new Map(prev);
            next.delete(fieldId);
            return next;
          })
        }
        inFlight={inFlight}
        elapsedMs={elapsedMs}
        lastError={error}
        sttSeconds={sttSeconds}
        passes={usage?.calls ?? 0}
        costUsd={usage?.costUsd ?? 0}
      />
    </div>
  );
}
